import { useMemo, useState } from "react";
import { Box, Button, ButtonGroup, Typography, useTheme } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { tokens } from "../../../../theme";

const PIE_COLORS = ["#4cceac", "#6870fa", "#db4f4a", "#f4a261", "#2e7c67", "#3e4396", "#e2726e"];

const SalesCharts = ({ data }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [groupDimension, setGroupDimension] = useState("company");

  const periodData = useMemo(() => {
    const map = new Map();
    data.forEach((row) => {
      const key = row.period;
      const entry = map.get(key) || { period: key, facturado: 0, cobrado: 0 };
      entry.facturado += Number(row.total_facturado) || 0;
      entry.cobrado += Number(row.total_cobrado) || 0;
      map.set(key, entry);
    });
    return Array.from(map.values()).sort((a, b) => a.period.localeCompare(b.period));
  }, [data]);

  const distributionData = useMemo(() => {
    const map = new Map();
    data.forEach((row) => {
      const key = groupDimension === "company" ? row.company_name : row.country_name;
      const entry = map.get(key) || { name: key, value: 0 };
      entry.value += Number(row.total_facturado) || 0;
      map.set(key, entry);
    });
    return Array.from(map.values());
  }, [data, groupDimension]);

  return (
    <Box
      display="grid"
      gridTemplateColumns={{ xs: "1fr", md: "1.4fr 1fr" }}
      gap={3}
      mb={3}
    >
      <Box backgroundColor={colors.primary[400]} borderRadius="12px" p={3}>
        <Typography variant="h6" mb={2}>
          Facturado vs Cobrado por período
        </Typography>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={periodData}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
            <XAxis dataKey="period" stroke={colors.grey[100]} />
            <YAxis stroke={colors.grey[100]} />
            <Tooltip
              contentStyle={{ backgroundColor: colors.primary[700], border: "none" }}
              formatter={(value) => `$${Number(value).toFixed(2)}`}
            />
            <Legend />
            <Bar
              dataKey="facturado"
              name="Facturado"
              fill={colors.blueAccent[400]}
              radius={[6, 6, 0, 0]}
              animationDuration={600}
            />
            <Bar
              dataKey="cobrado"
              name="Cobrado"
              fill={colors.greenAccent[400]}
              radius={[6, 6, 0, 0]}
              animationDuration={600}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box backgroundColor={colors.primary[400]} borderRadius="12px" p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Distribución de facturación</Typography>
          <ButtonGroup size="small">
            <Button
              variant={groupDimension === "company" ? "contained" : "outlined"}
              color="inherit"
              onClick={() => setGroupDimension("company")}
            >
              Empresa
            </Button>
            <Button
              variant={groupDimension === "country" ? "contained" : "outlined"}
              color="inherit"
              onClick={() => setGroupDimension("country")}
            >
              País
            </Button>
          </ButtonGroup>
        </Box>
        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <Pie
              data={distributionData}
              dataKey="value"
              nameKey="name"
              innerRadius={60}
              outerRadius={110}
              paddingAngle={2}
              animationDuration={600}
            >
              {distributionData.map((entry, index) => (
                <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: colors.primary[700], border: "none" }}
              formatter={(value) => `$${Number(value).toFixed(2)}`}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default SalesCharts;
